// =============================================================================
// Copyright : 2026 semantics GmbH
// Purpose   : search module reducers
// =============================================================================
import { handleAction, handleSubState } from 'uix/actions';
import { reduceMergeReducers } from 'uix/utils/reducers';
import { persistReducer } from 'uix/persist/redux';
import { keys, values } from 'ldash';
import log from 'uix/logging';
import { nlsqlStoreThread, nlsqlDropThread, nlsqlClearThreads } from './actions';
/* NL-SQL conversation store, keyed by the same thread id the backend logs.

Transcripts live in their own persisted slice instead of the ui/persist store on
purpose: that store keeps everything under a single `byId` key, so redux-persist
re-serializes the whole map on every ui/setValue - which includes every
keystroke in a persisted input. Transcripts carry result rows, so parking them
there would put a multi-MB JSON.stringify on the typing path. Own key + throttle
keeps those writes to roughly one per completed question.
*/ const PERSIST_THROTTLE = 2000;
// Bounds against unbounded growth over months. The only cost that scales with
// the total is the JSON.parse at app boot (~15 ms per 5 MB), so the byte
// ceiling is deliberately generous - a backstop, not a budget.
const MAX_THREADS = 25;
const MAX_BYTES = 50 * 1024 * 1024;
const threadBytes = (thread)=>{
    try {
        return JSON.stringify(thread).length;
    } catch (e) {
        log.exception('cannot size nlsql thread', e);
        return 0;
    }
};
const prune = (byId)=>{
    /* evict least recently used first, by dt (last write) rather than by thread
  id (creation). Ids are time-prefixed and would sort chronologically, but
  resuming an old conversation must not leave it first in line for eviction.
  */ let threadIds = keys(byId).sort((a, b)=>(byId[a].dt || 0) - (byId[b].dt || 0));
    let total = values(byId).reduce((sum, thread)=>sum + (thread.bytes || 0), 0);
    while(threadIds.length > MAX_THREADS || total > MAX_BYTES && threadIds.length > 1){
        let lru = threadIds.shift();
        total -= byId[lru]?.bytes || 0;
        let { [lru]: _dropped, ...rest } = byId;
        byId = rest;
    }
    return byId;
};
const threadReducers = [
    handleAction(nlsqlStoreThread, ({ byId, ...nlsql }, { payload })=>{
        let { threadId, thread } = payload;
        if (!threadId) {
            return {
                ...nlsql,
                byId
            };
        }
        let stored = {
            ...thread,
            threadId,
            dt: new Date().valueOf()
        };
        // size once, on write - so prune() never has to walk the whole store
        stored = {
            ...stored,
            bytes: threadBytes(stored)
        };
        return {
            ...nlsql,
            byId: prune({
                ...byId,
                [threadId]: stored
            })
        };
    }),
    handleAction(nlsqlDropThread, ({ byId, ...nlsql }, { payload })=>{
        let { [payload.threadId]: _dropped, ...rest } = byId;
        return {
            ...nlsql,
            byId: rest
        };
    }),
    handleAction(nlsqlClearThreads, ({ byId, ...nlsql })=>({
            ...nlsql,
            byId: {}
        }))
];
export const data = [
    handleSubState('nlsql', [
        persistReducer(reduceMergeReducers(...threadReducers), {
            key: 'uix_nlsql',
            whitelist: [
                'byId'
            ],
            throttle: PERSIST_THROTTLE
        })
    ], {
        byId: {}
    })
];
